import { defineStore } from 'pinia'
import defaultEnDictionary from '@/locales/golf/en'
import defaultAzDictionary from '@/locales/golf/az'
import defaultRuDictionary from '@/locales/golf/ru'
import { getGolfTranslations } from '@/api/golf-localization'

export type GolfLanguage = 'en' | 'az' | 'ru'

export interface GolfLanguageOption {
  code: GolfLanguage
  label: string
  flag: string
}

export const GOLF_LANGUAGES: GolfLanguageOption[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'az', label: 'Azərbaycan', flag: '🇦🇿' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
]

export const LS_GOLF_LANGUAGE_KEY = 'golf_selected_language'

const KEY_ALIASES: Record<string, string> = {
  'golf.menu.fleet': 'golf.fleet.title',
  'golf.fleet.title': 'golf.menu.fleet',
  'golf.menu.dashboard': 'golf.dashboard.title',
  'golf.dashboard.title': 'golf.menu.dashboard',
  'golf.menu.trips': 'golf.trips.title',
  'golf.trips.title': 'golf.menu.trips',
  'golf.menu.reserves': 'golf.reserves.title',
  'golf.reserves.title': 'golf.menu.reserves',
  'golf.menu.tariffs_settings': 'golf.tariffs.title',
  'golf.tariffs.title': 'golf.menu.tariffs_settings',
  'golf.menu.financial_reports': 'golf.financial.title',
  'golf.financial.title': 'golf.menu.financial_reports',
  'golf.menu.users_access': 'golf.roles.title',
  'golf.roles.title': 'golf.menu.users_access',
  'golf.menu.map': 'golf.map.title',
  'golf.map.title': 'golf.menu.map',
  'golf.trips.lifestyle_status': 'golf.trips.lifecycle_status',
}

export const useGolfI18nStore = defineStore('golfI18n', {
  state: () => {
    let savedLang: GolfLanguage = 'en'
    try {
      const stored = localStorage.getItem(LS_GOLF_LANGUAGE_KEY)
      if (stored === 'en' || stored === 'az' || stored === 'ru') {
        savedLang = stored
      }
    } catch (e) {
      // ignore
    }

    return {
      currentLanguage: savedLang as GolfLanguage,
      remoteTranslations: {} as Record<string, Record<string, string>>,
      isLoading: false,
      isLoaded: false,
      error: null as string | null,
    }
  },

  getters: {
    currentLanguageOption: (state): GolfLanguageOption => {
      return (
        GOLF_LANGUAGES.find(l => l.code === state.currentLanguage) ||
        GOLF_LANGUAGES[0]
      )
    },
  },

  actions: {
    setLanguage(lang: GolfLanguage) {
      if (this.currentLanguage === lang) return
      this.currentLanguage = lang
      try {
        localStorage.setItem(LS_GOLF_LANGUAGE_KEY, lang)
      } catch (e) {
        // ignore
      }
    },

    async fetchTranslations() {
      this.isLoading = true
      this.error = null
      try {
        const response = await getGolfTranslations()
        if (response && response.data && (response.data as any).data) {
          this.remoteTranslations = (response.data as any).data
          this.isLoaded = true
        }
      } catch (err) {
        console.warn(
          'Could not fetch remote golf translations, using defaults',
          err
        )
        this.error =
          err instanceof Error ? err.message : 'Failed to load translations'
      } finally {
        this.isLoading = false
      }
    },

    t(key: string, params?: Record<string, string | number>): string {
      const lang = this.currentLanguage
      const remoteForLang = this.remoteTranslations[lang]
      const remoteForEn = this.remoteTranslations['en']
      const fallbackLocalLang =
        lang === 'az'
          ? (defaultAzDictionary as Record<string, string>)
          : lang === 'ru'
          ? (defaultRuDictionary as Record<string, string>)
          : (defaultEnDictionary as Record<string, string>)
      const fallbackEn = (defaultEnDictionary as Record<string, string>)[key]

      const isValid = (val?: string): boolean => {
        if (!val || typeof val !== 'string') return false
        const trimmed = val.trim()
        return (
          trimmed !== '' &&
          trimmed !== '"' &&
          trimmed !== '""' &&
          trimmed !== "'" &&
          trimmed !== "''"
        )
      }

      const aliasKey = KEY_ALIASES[key]

      let result =
        (remoteForLang && isValid(remoteForLang[key])
          ? remoteForLang[key].trim()
          : '') ||
        (aliasKey && remoteForLang && isValid(remoteForLang[aliasKey])
          ? remoteForLang[aliasKey].trim()
          : '') ||
        (fallbackLocalLang && isValid(fallbackLocalLang[key])
          ? fallbackLocalLang[key].trim()
          : '') ||
        (aliasKey && fallbackLocalLang && isValid(fallbackLocalLang[aliasKey])
          ? fallbackLocalLang[aliasKey].trim()
          : '') ||
        (remoteForEn && isValid(remoteForEn[key])
          ? remoteForEn[key].trim()
          : '') ||
        (aliasKey && remoteForEn && isValid(remoteForEn[aliasKey])
          ? remoteForEn[aliasKey].trim()
          : '') ||
        fallbackEn ||
        (aliasKey
          ? (defaultEnDictionary as Record<string, string>)[aliasKey]
          : '') ||
        key

      if (params && typeof result === 'string') {
        Object.entries(params).forEach(([paramKey, paramVal]) => {
          result = result.replace(
            new RegExp(`(\\{\\{|\\{)${paramKey}(\\}\\}|\\})`, 'g'),
            String(paramVal)
          )
        })
      }

      return result
    },
  },
})

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $t: (key: string, params?: Record<string, string | number>) => string
  }
}
